import { useCallback, useMemo, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  subMonths,
} from "date-fns";
import { es } from "date-fns/locale";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  Minus,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import * as XLSX from "xlsx";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

import { useClientDebtHistory } from "./hooks/useClientDebtHistory";
import type { ClientDebtHistoryEntry } from "./types";

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatCompactCurrency = (amount: number): string => {
  if (amount >= 1000000000) {
    return `$${(amount / 1000000000).toFixed(1)}B`;
  }
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  }
  return formatCurrency(amount);
};

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

interface CalendarGridProps {
  currentMonth: Date;
  entries: ClientDebtHistoryEntry[];
  maxDebt: number;
  minDebt: number;
}

const CalendarGrid = ({
  currentMonth,
  entries,
  maxDebt,
  minDebt,
}: CalendarGridProps) => {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Create a map of date -> entry for quick lookup
  const entryMap = useMemo(() => {
    const map = new Map<string, ClientDebtHistoryEntry>();
    entries.forEach((entry) => {
      map.set(entry.snapshot_date, entry);
    });
    return map;
  }, [entries]);

  // Create a map of date -> previous day entry for calculating changes
  const changeMap = useMemo(() => {
    const map = new Map<string, number>();
    const sortedEntries = [...entries].sort(
      (a, b) =>
        new Date(a.snapshot_date).getTime() -
        new Date(b.snapshot_date).getTime()
    );
    for (let i = 1; i < sortedEntries.length; i++) {
      const current = sortedEntries[i];
      const prev = sortedEntries[i - 1];
      map.set(current.snapshot_date, current.debt_amount - prev.debt_amount);
    }
    return map;
  }, [entries]);

  // Get the day of the week for the first day (0 = Sunday, adjust for Monday start)
  const startDayOfWeek = getDay(monthStart);
  const adjustedStartDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

  // Calculate color intensity based on debt amount
  const getDebtColor = (amount: number): string => {
    if (maxDebt === minDebt) return "bg-blue-100";
    const intensity = (amount - minDebt) / (maxDebt - minDebt);
    if (intensity < 0.2) return "bg-green-100 border-green-200";
    if (intensity < 0.4) return "bg-green-200 border-green-300";
    if (intensity < 0.6) return "bg-yellow-100 border-yellow-200";
    if (intensity < 0.8) return "bg-orange-100 border-orange-200";
    return "bg-red-100 border-red-200";
  };

  return (
    <div className="space-y-2">
      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="py-2 text-center text-sm font-medium text-muted-foreground"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-1">
        {/* Empty cells for days before the month starts */}
        {Array.from({ length: adjustedStartDay }).map((_, i) => (
          <div key={`empty-${i}`} className="aspect-square" />
        ))}

        {/* Days of the month */}
        {daysInMonth.map((day) => {
          const dateStr = format(day, "yyyy-MM-dd");
          const entry = entryMap.get(dateStr);
          const change = changeMap.get(dateStr);
          const dayNumber = format(day, "d");

          if (!entry) {
            return (
              <div
                key={dateStr}
                className="flex aspect-square flex-col items-center justify-center rounded-md border border-dashed border-gray-200 text-muted-foreground"
              >
                <span className="text-sm">{dayNumber}</span>
                <span className="text-xs">-</span>
              </div>
            );
          }

          return (
            <TooltipProvider key={dateStr}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      "flex aspect-square cursor-pointer flex-col items-center justify-center rounded-md border transition-all hover:scale-105 hover:shadow-md",
                      getDebtColor(entry.debt_amount)
                    )}
                  >
                    <span className="text-sm font-medium">{dayNumber}</span>
                    <span className="font-mono text-xs font-semibold">
                      {formatCompactCurrency(entry.debt_amount)}
                    </span>
                    {change !== undefined && change !== 0 && (
                      <span
                        className={cn(
                          "font-mono text-[10px]",
                          change > 0 ? "text-red-600" : "text-green-600"
                        )}
                      >
                        {change > 0 ? "+" : ""}
                        {formatCompactCurrency(change)}
                      </span>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-center">
                  <div className="font-medium">
                    {format(day, "EEEE, d 'de' MMMM", { locale: es })}
                  </div>
                  <div className="text-sm">
                    Deuda: {formatCurrency(entry.debt_amount)}
                  </div>
                  {change !== undefined && change !== 0 && (
                    <div
                      className={cn(
                        "text-sm",
                        change > 0 ? "text-red-400" : "text-green-400"
                      )}
                    >
                      Cambio: {change > 0 ? "+" : ""}
                      {formatCurrency(change)}
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    </div>
  );
};

export default function ClientDebtHistoryPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  // Start with current month
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  // Calculate date range for the current view (3 months: previous, current, next)
  const startDate = useMemo(
    () => format(startOfMonth(subMonths(currentMonth, 1)), "yyyy-MM-dd"),
    [currentMonth]
  );
  const endDate = useMemo(
    () => format(endOfMonth(addMonths(currentMonth, 1)), "yyyy-MM-dd"),
    [currentMonth]
  );

  const { data, isLoading, error } = useClientDebtHistory({
    clientId,
    startDate,
    endDate,
  });

  const handlePreviousMonth = useCallback(() => {
    setCurrentMonth((prev) => subMonths(prev, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setCurrentMonth((prev) => addMonths(prev, 1));
  }, []);

  const handleExportExcel = useCallback(() => {
    if (!data?.entries || data.entries.length === 0) return;

    const sortedEntries = [...data.entries].sort(
      (a, b) =>
        new Date(b.snapshot_date).getTime() -
        new Date(a.snapshot_date).getTime()
    );

    const rows = sortedEntries.map((entry, index) => {
      const prevEntry = sortedEntries[index + 1];
      const change = prevEntry ? entry.debt_amount - prevEntry.debt_amount : 0;
      return {
        Fecha: entry.snapshot_date,
        Deuda: entry.debt_amount,
        Cambio: change,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Historial de Deuda");

    XLSX.writeFile(
      workbook,
      `historial-deuda-${data.clientInfo?.client_rut || clientId}.xlsx`
    );
  }, [data, clientId]);

  // Filter entries for the current month
  const currentMonthEntries = useMemo(() => {
    if (!data?.entries) return [];
    return data.entries.filter((entry) =>
      isSameMonth(parseISO(entry.snapshot_date), currentMonth)
    );
  }, [data?.entries, currentMonth]);

  // Calculate statistics for current month
  const stats = useMemo(() => {
    if (!currentMonthEntries || currentMonthEntries.length === 0) {
      return { current: 0, max: 0, min: 0, avg: 0, change: 0 };
    }

    const amounts = currentMonthEntries.map((e) => e.debt_amount);
    const sortedByDate = [...currentMonthEntries].sort(
      (a, b) =>
        new Date(b.snapshot_date).getTime() -
        new Date(a.snapshot_date).getTime()
    );
    const current = sortedByDate[0]?.debt_amount || 0;
    const oldest = sortedByDate[sortedByDate.length - 1]?.debt_amount || 0;

    return {
      current,
      max: Math.max(...amounts),
      min: Math.min(...amounts),
      avg: amounts.reduce((a, b) => a + b, 0) / amounts.length,
      change: current - oldest,
    };
  }, [currentMonthEntries]);

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center text-red-500">
          Error al cargar el historial: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/daily-debt-snapshots")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">
            Historial de Deuda del Cliente
          </h1>
          {data?.clientInfo ? (
            <p className="text-muted-foreground">
              <a
                href={`/clients/${clientId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors hover:text-primary hover:underline"
              >
                {data.clientInfo.client_name}
              </a>
              {" - "}
              {data.clientInfo.client_rut}
            </p>
          ) : isLoading ? (
            <Skeleton className="mt-1 h-5 w-64" />
          ) : null}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportExcel}
          disabled={!data?.entries || data.entries.length === 0}
        >
          <Download className="mr-2 h-4 w-4" />
          Exportar Excel
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Deuda Actual</div>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-32" />
              ) : (
                formatCurrency(stats.current)
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Cambio en Mes</div>
            <div className="flex items-center gap-2">
              {isLoading ? (
                <Skeleton className="h-8 w-32" />
              ) : (
                <>
                  <span
                    className={cn(
                      "text-2xl font-bold",
                      stats.change > 0 && "text-red-600",
                      stats.change < 0 && "text-green-600"
                    )}
                  >
                    {stats.change > 0 ? "+" : ""}
                    {formatCurrency(stats.change)}
                  </span>
                  {stats.change > 0 ? (
                    <TrendingUp className="h-5 w-5 text-red-600" />
                  ) : stats.change < 0 ? (
                    <TrendingDown className="h-5 w-5 text-green-600" />
                  ) : (
                    <Minus className="h-5 w-5 text-muted-foreground" />
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Máximo del Mes</div>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-32" />
              ) : (
                formatCurrency(stats.max)
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">
              Promedio del Mes
            </div>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-32" />
              ) : (
                formatCurrency(stats.avg)
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar Grid */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <Button variant="outline" size="icon" onClick={handlePreviousMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <CardTitle className="text-lg capitalize">
            {format(currentMonth, "MMMM yyyy", { locale: es })}
          </CardTitle>
          <Button variant="outline" size="icon" onClick={handleNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: 35 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-md" />
              ))}
            </div>
          ) : (
            <CalendarGrid
              currentMonth={currentMonth}
              entries={data?.entries || []}
              maxDebt={stats.max}
              minDebt={stats.min}
            />
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
            <span className="text-muted-foreground">Nivel de deuda:</span>
            <div className="flex items-center gap-1">
              <div className="h-4 w-4 rounded border border-green-200 bg-green-100" />
              <span>Bajo</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-4 w-4 rounded border border-green-300 bg-green-200" />
              <span>Bajo-Medio</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-4 w-4 rounded border border-yellow-200 bg-yellow-100" />
              <span>Medio</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-4 w-4 rounded border border-orange-200 bg-orange-100" />
              <span>Medio-Alto</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-4 w-4 rounded border border-red-200 bg-red-100" />
              <span>Alto</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-4 w-4 rounded border border-dashed border-gray-300" />
              <span>Sin datos</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
